/**
 * Collects the attributes from the component rendered into this page
 */
interface TefCustomElement extends CustomElementConstructor {
  version?: string;
}

interface TefElement extends Element {
  version?: string;
}

interface MfeLogPayloadDetail {
  init: Record<string, unknown>;
  dom: Record<string, unknown>[];
}

export interface MfeLogPayload {
  tag: string;
  version: string;
  scriptSrc: string;
  document: {
    origin: string;
    hostname: string;
    pathname: string;
  }
  message: string;
  details: MfeLogPayloadDetail;
}

// A list of tag name PREFIXES that are searched
// for shadowRoots and deep searched for a tag (see below)
// This is here to minimize the performance impact of looking at the complete DOM including all shadowRoots.
const NODES_TO_SEARCH_FOR_SHADOWROOT = [
  'one-',
].map((m) => m.toUpperCase());

/**
 * Finds tags by searching the page includign all shadowRoots that match a given tag prefix
 */
export const deepFindTag = (tagName: string, root: Element | ShadowRoot = window.document.body): Element[] => {
  // Nodes in the light DOM (NOT in nested shadowRoot)
  const directNodes = [...root.querySelectorAll(tagName)];

  // Nodes inside the root, have a tagName of one of NODES_TO_SEARCH_FOR_SHADOWROOT (eg. "one-*") and have a shadowRoot:
  // Those are recursivly digged through for the tagName ...
  const nestedNodes: Element[] = [...root.querySelectorAll('*')]
    .filter((node) => node.shadowRoot && NODES_TO_SEARCH_FOR_SHADOWROOT.some((prefix) => node.tagName.startsWith(prefix)))
    .reduce((allNodes: Element[], currentNode) => allNodes.concat(deepFindTag(tagName, currentNode.shadowRoot!)), []);

  return directNodes.concat(nestedNodes);
};

/**
 * Mask potentially sensible fields
 */
export const maskSensibleValue = (attributeName: string, value: string | null = null) => {
  if (/token/i.test(attributeName) && value && value.length > 0) {
    value = `${String(value).substring(0, 10)}...`; // eslint-disable-line 
  }
  return value;
};

/**
 * Generate the payload for api-consent POST /service/mfe/log
 * This includes the current DOM state and the init parameters of the current initualizing microfrontend
 */
export const getMfeLogPayload = (tagName: string, initData: Record<string, string | null | undefined> = {}) => {
  const { origin, hostname, pathname } = new URL(window.document.URL);

  // Sanitize initData
  const initDataClean: typeof initData = {};
  Object.keys(initData).forEach((keyName) => {
    initDataClean[keyName] = maskSensibleValue(keyName, initData[keyName]);
  });

  const mfeLogPayload: MfeLogPayload = {
    tag: tagName,
    version: '0.0.0',
    scriptSrc: 'unknown',
    document: { origin, hostname, pathname },
    message: 'init',
    details: {
      dom: [],
      init: initDataClean,
    },
  };

  const microfrontendNodes = deepFindTag(tagName, window.document.body);
  // Use the first node for version detection (assuming all instances have the same version)
  const firstNode = microfrontendNodes[0] as TefElement;

  // Detect MFE version (two possibilities so far)
  const customElement = window.customElements.get(tagName) as TefCustomElement;
  if (customElement && typeof customElement.version !== 'undefined') {
    mfeLogPayload.version = customElement.version;
  } else if (firstNode && typeof firstNode.version !== 'undefined') {
    mfeLogPayload.version = firstNode.version;
  }

  // Detect "src" attribute of the <script> tag
  const scriptTag = document.querySelector(`script[src*='${tagName}']`);
  // web-test-runner will fail if we do not guard this
  if (scriptTag) {
    mfeLogPayload.scriptSrc = scriptTag.getAttribute('src')!; // TS: We know that "src" is there because we used it in the selector
  }

  microfrontendNodes.forEach((microfrontendNode) => {
    const nodeDetails: Record<string, unknown> = {};

    microfrontendNode.getAttributeNames().forEach((attributeName: string) => {
      const attributeValue = microfrontendNode.getAttribute(attributeName);
      nodeDetails[attributeName] = maskSensibleValue(attributeName, attributeValue);
    });

    mfeLogPayload.details.dom.push(nodeDetails);
  });

  return mfeLogPayload;
};
